import { FC, useEffect, useState } from 'react';
import { getStockOrders } from '../../../../../../services/stockOrders';
import StockOrderCard from '../../../StockOrderCard/StockOrderCard';
import { IStockOrderData, SHIPMENT_STATUS_STOCK_ORDER } from '../../../../../../types/stockOrders';
import { NudosSearchBar } from '../../../../../../components/NudosComponents';
import BordOneToneIcon from '../../../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import useSWR from 'swr';
import { StockOrderPropertyToSearch } from '../../../../../../types/stockOrders';
import './CancelledOrdersOverlay.scss';

interface ICancelledOrdersOverlay {
  onClose: () => void;
  isOpen: boolean;
  onMatchesFound: (hasMatches: boolean) => void;
  searchText: string;
  propertyToFilterSearch: StockOrderPropertyToSearch | null;
}

/**
 * Componente que muestra un overlay con la lista de órdenes canceladas
 * @param {ICancelledOrdersOverlay} props - Props del componente
 * @returns {JSX.Element} Componente de overlay de órdenes canceladas
 */
const CancelledOrdersOverlay: FC<ICancelledOrdersOverlay> = ({
  onClose,
  isOpen,
  onMatchesFound,
  searchText,
  propertyToFilterSearch
}) => {
  const [searchTerm, setSearchTerm] = useState(searchText);
  const [filteredOrders, setFilteredOrders] = useState<IStockOrderData[]>([]);
  const [isClosing, setIsClosing] = useState(false);
  const [hasUserTyped, setHasUserTyped] = useState(false);

  useEffect(() => {
    if (!hasUserTyped) {
      setSearchTerm(searchText);
    }
  }, [searchText, hasUserTyped]);

  const { data: cancelledOrders } = useSWR(['stockOrders-cancelled', 5], () =>
    getStockOrders({ statusId: 5, filters: '', search: '', sort: '' })
  );

  const checkOrderMatch = (
    order: IStockOrderData,
    searchValue: string,
    property?: StockOrderPropertyToSearch | null
  ): boolean => {
    if (!searchValue) return true;

    const searchLower = searchValue.toLowerCase();
    const propertyToFilter = property?.propertyToFilter || 'orderId';

    switch (propertyToFilter) {
      case 'orderId':
        return order.orderId.toString().toLowerCase().includes(searchLower);
      case 'invoiceNumber':
        return order.orderId.toString().toLowerCase().includes(searchLower);
      case 'provider':
        return order.providerName.toLowerCase().includes(searchLower);
      default:
        return order.orderId.toString().toLowerCase().includes(searchLower);
    }
  };

  useEffect(() => {
    if (cancelledOrders) {
      const filtered = cancelledOrders.filter(order => {
        if (searchTerm === searchText && propertyToFilterSearch) {
          return checkOrderMatch(order, searchTerm, propertyToFilterSearch);
        }

        return checkOrderMatch(order, searchTerm);
      });
      setFilteredOrders(filtered);
    }
  }, [searchTerm, cancelledOrders, searchText, propertyToFilterSearch]);

  useEffect(() => {
    if (cancelledOrders && searchText && propertyToFilterSearch) {
      const hasMatches = cancelledOrders.some(order => checkOrderMatch(order, searchText, propertyToFilterSearch));
      onMatchesFound(hasMatches);
    } else {
      onMatchesFound(false);
    }
  }, [searchText, cancelledOrders, onMatchesFound, propertyToFilterSearch]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setHasUserTyped(false);
      onClose();
    }, 300); // Duración de la animación
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div className={`cancelledOrdersOverlay ${isClosing ? 'closing' : ''}`} onClick={handleOverlayClick}>
      <div className="cancelledOrdersOverlayContent">
        <div className="overlayContent relative">
          <div className="overlayHeader">
            <div className="headerLeft stockOrderTitle">
              <span>Órdenes de compra canceladas</span>
              <div className="orderCount">({filteredOrders.length})</div>
            </div>
            <div onClick={handleClose} className="closeButton">
              <BordOneToneIcon variant="x" standardSize={18} />
            </div>
          </div>

          <div className="searchSection">
            <NudosSearchBar
              defaultValue={searchTerm}
              handleChange={value => {
                setSearchTerm(value);
                setHasUserTyped(true);
              }}
              placeholder="Buscar por número de orden..."
            />
          </div>

          <div className="ordersList">
            {filteredOrders.map(order => (
              <StockOrderCard
                key={order.orderId}
                data={order}
                shipmentStatusName={SHIPMENT_STATUS_STOCK_ORDER.CANCELLED}
              />
            ))}
            {filteredOrders.length === 0 && <div className="noResults">No se encontraron órdenes canceladas</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelledOrdersOverlay;
